import LoggerService from '@/services/isomorphic/LoggerService';
import {
  PersonalizationService,
  type Decision,
  type ExperienceID
} from '@/services/isomorphic/PersonalizationService';
import type { IPage } from '@/services/models/Page';
import type { Nullable } from '@/type-utils';
import { useEffect, useMemo, useState } from 'react';
import { isNullish } from '@/utils/null-utils';

/** Options for the `useDecision` hook. */
export interface IUseDecisionOptions {
  /** The page that the experience is being rendered on. */
  page?: Nullable<IPage>;
}

/**
 * A hook that fetches the decision for the given experience ID.
 * @param experienceID - The experience ID to fetch a decision for.
 * @param options - The options for the hook.
 * @returns The decision object for the experience ID, or `null` if it is still loading.
 * @throws If there was an error fetching the decision.
 */
export function useDecision(
  experienceID: ExperienceID,
  options: IUseDecisionOptions = {}
): Decision | undefined {
  const { page } = options;

  // Extract page properties to create a stable dependency key
  // This prevents unnecessary re-renders when the page object reference changes but values remain the same
  const pageType = page?.pageType;
  const pageUrl = page?.url;
  const pageKey = useMemo(() => {
    if (isNullish(pageType) || isNullish(pageUrl)) return null;
    return `${pageType}:${pageUrl}`;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- We intentionally use extracted page properties
    // instead of the page object to avoid recomputing when only the object reference changes
  }, [pageType, pageUrl]);

  // While the decision is unknown it is null.
  const [decision, setDecision] = useState<Decision | undefined>();

  // Error indicator.
  const [errorInd, setErrorInd] = useState<{ error: unknown } | undefined>();

  useEffect(
    function getDecision() {
      let isStale = false;

      const fetchDecision = async (): Promise<void> => {
        setErrorInd(undefined);

        try {
          const decision =
            await PersonalizationService.getExperienceForCurrentUser(
              experienceID,
              page
            );

          if (isStale) return;
          setDecision(decision);
        } catch (error) {
          LoggerService.warn((error as Error).message);
          if (isStale) return;
          setErrorInd({ error });
        }
      };

      // eslint-disable-next-line no-void -- Intentionally floating promise; errors are handled via errorInd state
      void fetchDecision();

      return () => {
        isStale = true;
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- pageKey is derived from page properties,
    // so we use it instead of page to prevent duplicate requests when page object reference changes
    [experienceID, pageKey]
  );

  // If there was an indication of an error, throw it to the error boundary.
  if (errorInd) {
    throw errorInd.error;
  }

  return decision;
}
